import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { MdCheckCircle, MdCancel, MdAccessTime, MdSupervisorAccount, MdAssignment, MdDateRange, MdLocalShipping } from "react-icons/md";
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from '../../../utils/tokenManager';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const provinceCities = [
  'Techiman',
  'Nkoranza',
  'Kintampo',
  'Wenchi',
  'Nsawkaw',
  'Banda',
  'Nkrankwanta',
  'Danyame',
  'Berekum',
  'Dormaa',
  'Drobo',
  'Sampa',
  'Nalerigu',
  'Yamatwa',
  'Kofibadukrom',
  'Kwameseikrom',
  'Kwameprakrom'
];

const analysts = [
  'Dominic Homadzi',
  'Gabriel Amanor',
  'Christian Gatienu',
  'Emmanuel Narh',
  'Christian Asiamah',
  'Ernest Norvor',
  'Emmanuel Avi Kaledzi',
  'Francis Baidoo',
  'Philip Akoto',
  'Matthew Abochie'
];

const FieldAnalysis = () => {
  const [form, setForm] = useState({
    date: '',
    orderNo: '',
    region: '',
    provinceCity: '',
    analystName: '',
    nameAgent: '',
    truckNo: '',
    bagsNo: '',
    driverName: '',
    driverContact: '',
    outturnLbs: '',
    departure: false,
    nutCount: '',
    goodG: '',
    spottedG: '',
    immatureG: '',
    impuritiesG: '',
    boredG: '',
    rottenG: '',
    voidG: '',
    oilG: '',
    shellG: '',
    totalG: '',
    tdrPercent: '',
    moisturePercent: '',
    additionalComments: '',
    agentSign: '',
    agentDate: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

  // Fetch analyst full name from profile (like Settings.jsx)
  useEffect(() => {
    const fetchAnalystName = async () => {
      try {
        const loginData = localStorage.getItem('usibras_login');
        const storedUserData = localStorage.getItem('usibras_user_data');
        if (!loginData && !storedUserData) return;

        let decodedToken;
        if (loginData) {
          try {
            const parsed = JSON.parse(loginData);
            if (parsed.userData) {
              decodedToken = jwtDecode(parsed.userData);
            } else {
              decodedToken = jwtDecode(loginData);
            }
          } catch {
            decodedToken = jwtDecode(loginData);
          }
        } else if (storedUserData) {
          decodedToken = jwtDecode(storedUserData);
        }

        const userId = decodedToken?.id;
        if (!userId) return;

        const response = await axios.get(`https://usibras-web-backend.onrender.com/api/profile/${userId}`);
        const { user } = response.data || {};
        if (!user) return;

        const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || '';
        if (fullName) {
          setForm(prev => ({ ...prev, analystName: fullName }));
        }
      } catch (err) {
        // Soft fail; keep existing value
        console.error('Failed to fetch analyst profile:', err);
      }
    };

    fetchAnalystName();
  }, []);

  // Calculate defective rate and quality rating
  const calculateQualityRating = (formData) => {
    const spotted = parseFloat(formData.spottedG) || 0;
    const immature = parseFloat(formData.immatureG) || 0;
    const rotten = parseFloat(formData.rottenG) || 0;
    const void_ = parseFloat(formData.voidG) || 0;
    const impurities = parseFloat(formData.impuritiesG) || 0;
    const bored = parseFloat(formData.boredG) || 0;
    const oily = parseFloat(formData.oilG) || 0;

    // Formula: [ (1/2 * (spotted + immature)) + rotten + void + impurities + bored + oily ] / 1000g * 100%
    const defectiveSum = (0.5 * (spotted + immature)) + rotten + void_ + impurities + bored + oily;
    const defectiveRate = (defectiveSum / 1000) * 100;

    let qualityRating = '';
    let ratingColor = '';
    
    // Check if any defective field has been entered
    const hasDefectiveData = spotted > 0 || immature > 0 || rotten > 0 || void_ > 0 || impurities > 0 || bored > 0 || oily > 0;
    
    // Only show rating if data has been entered
    if (hasDefectiveData) {
      if (defectiveRate >= 0 && defectiveRate <= 6.9) {
        qualityRating = 'Very good';
        ratingColor = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      } else if (defectiveRate >= 7 && defectiveRate <= 10.9) {
        qualityRating = 'Good';
        ratingColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      } else if (defectiveRate >= 11 && defectiveRate <= 15.9) {
        qualityRating = 'Regular';
        ratingColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      } else if (defectiveRate >= 16 && defectiveRate <= 20.9) {
        qualityRating = 'Bad';
        ratingColor = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      } else if (defectiveRate >= 21) {
        qualityRating = 'Very bad';
        ratingColor = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      }
    }

    return {
      defectiveRate: defectiveRate > 0 ? defectiveRate.toFixed(2) : '0.00',
      qualityRating,
      ratingColor
    };
  };

  // Quality Rating Badge Component
  const QualityRatingBadge = ({ defectiveRate, qualityRating, ratingColor }) => {
    if (!qualityRating) return null;
    
    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm ${ratingColor}`}>
        <span>{qualityRating}</span>
        <span className="text-xs opacity-75">({defectiveRate}%)</span>
      </div>
    );
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => {
      const updated = { 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      };
      
      // Auto-calculate total when any weight field (ending in G) changes
      const weightFields = ['goodG', 'spottedG', 'immatureG', 'impuritiesG', 'boredG', 'rottenG', 'voidG', 'oilG', 'shellG'];
      if (weightFields.includes(name)) {
        const values = [
          updated.goodG || 0,
          updated.spottedG || 0,
          updated.immatureG || 0,
          updated.impuritiesG || 0,
          updated.boredG || 0,
          updated.rottenG || 0,
          updated.voidG || 0,
          updated.oilG || 0,
          updated.shellG || 0
        ];
        
        const total = values.reduce((sum, val) => {
          const num = parseFloat(val) || 0;
          return sum + num;
        }, 0);
        
        updated.totalG = total > 0 ? total.toFixed(2) : '';
      }
      
      // Auto-calculate TDR (%) when defective fields change
      // Formula: [ (1/2 * (spotted + immature)) + rotten + void + impurities + bored + oily ] / 1000g * 100%
      const defectiveFields = ['spottedG', 'immatureG', 'rottenG', 'voidG', 'impuritiesG', 'boredG', 'oilG'];
      if (defectiveFields.includes(name)) {
        const spotted = parseFloat(updated.spottedG) || 0;
        const immature = parseFloat(updated.immatureG) || 0;
        const rotten = parseFloat(updated.rottenG) || 0;
        const void_ = parseFloat(updated.voidG) || 0;
        const impurities = parseFloat(updated.impuritiesG) || 0;
        const bored = parseFloat(updated.boredG) || 0;
        const oily = parseFloat(updated.oilG) || 0;

        const defectiveSum = (0.5 * (spotted + immature)) + rotten + void_ + impurities + bored + oily;
        const tdrPercent = (defectiveSum / 1000) * 100;
        
        updated.tdrPercent = tdrPercent.toFixed(2);
      }
      
      // Auto-calculate Outturn (LBS) when goodG, spottedG, or immatureG changes
      // Formula: (Good nuts + (spotted nuts + immature) / 2) * 0.176
      const outturnFields = ['goodG', 'spottedG', 'immatureG'];
      if (outturnFields.includes(name)) {
        const good = parseFloat(updated.goodG) || 0;
        const spotted = parseFloat(updated.spottedG) || 0;
        const immature = parseFloat(updated.immatureG) || 0;
        
        const outturnLbs = (good + (spotted + immature) / 2) * 0.176;
        updated.outturnLbs = outturnLbs > 0 ? outturnLbs.toFixed(2) : '';
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setToast({ show: false, message: '', type: 'info' });

    // Validation
    if (!form.date) {
      setToast({
        show: true,
        message: 'Please select a date.',
        type: 'error'
      });
      setIsSubmitting(false);
      return;
    }

    if (!form.region) {
      setToast({
        show: true,
        message: 'Please select an RCN type.',
        type: 'error'
      });
      setIsSubmitting(false);
      return;
    }

    if (!form.provinceCity) {
      setToast({
        show: true,
        message: 'Please select a province/city.',
        type: 'error'
      });
      setIsSubmitting(false);
      return;
    }

    if (!form.nameAgent) {
      setToast({
        show: true,
        message: 'Please enter the name/agent.',
        type: 'error'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Calculate quality rating
      const qualityData = calculateQualityRating(form);
      
      // Convert grams to percentages (based on 1000g sample) before sending to API
      const convertGramsToPercent = (grams) => {
        const gramsValue = parseFloat(grams) || 0;
        return gramsValue > 0 ? ((gramsValue / 1000) * 100) : null;
      };

      // Map form data to API parameters
      const apiData = {
        date: form.date,
        order_no: form.orderNo || null,
        region: form.region,
        province_city: form.provinceCity,
        analyst_name: form.analystName || null,
        name_agent: form.nameAgent,
        truck_no: form.truckNo || null,
        bags_no: form.bagsNo ? parseInt(form.bagsNo) : null,
        driver_name: form.driverName || null,
        driver_contact: form.driverContact || null,
        outturn_lbs: form.outturnLbs ? parseFloat(form.outturnLbs) : null,
        departure: form.departure,
        nut_count: form.nutCount ? parseInt(form.nutCount) : null,
        good_g: convertGramsToPercent(form.goodG),
        spotted_g: convertGramsToPercent(form.spottedG),
        immature_g: convertGramsToPercent(form.immatureG),
        impurities_g: convertGramsToPercent(form.impuritiesG),
        bored_g: convertGramsToPercent(form.boredG),
        rotten_g: convertGramsToPercent(form.rottenG),
        void_g: convertGramsToPercent(form.voidG),
        oil_g: convertGramsToPercent(form.oilG),
        shell_g: convertGramsToPercent(form.shellG),
        total_g: convertGramsToPercent(form.totalG),
        tdr_percent: form.tdrPercent ? parseFloat(form.tdrPercent) : parseFloat(qualityData.defectiveRate) || 0,
        moisture_percent: form.moisturePercent ? parseFloat(form.moisturePercent) : null,
        additional_comments: form.additionalComments || null,
        agent_sign: form.nameAgent, // Using nameAgent as agent sign
        agent_date: form.agentDate || form.date,
        defective_rate: parseFloat(qualityData.defectiveRate) || 0,
        quality_rating: qualityData.qualityRating || null,
        form_id: form.analystName
      };

      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        setToast({
          show: true,
          message: 'Authentication token not found. Please login again.',
          type: 'error'
        });
        setIsSubmitting(false);
        return;
      }

      // Make API request with authentication
      const response = await fetch('https://usibras-web-backend.onrender.com/api/field-analysis', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      const data = await response.json();
      
      if (response.ok) {
        setToast({
          show: true,
          message: data.message || 'Field analysis submitted successfully!',
          type: 'success'
        });
        
        // Reset form after successful submission
        setForm({
          date: '',
          orderNo: '',
          region: '',
          provinceCity: '',
          analystName: form.analystName,
          nameAgent: '',
          truckNo: '',
          bagsNo: '',
          driverName: '',
          driverContact: '',
          outturnLbs: '',
          departure: false,
          nutCount: '',
          goodG: '',
          spottedG: '',
          immatureG: '',
          impuritiesG: '',
          boredG: '',
          rottenG: '',
          voidG: '',
          oilG: '',
          shellG: '',
          totalG: '',
          tdrPercent: '',
          moisturePercent: '',
          additionalComments: '',
          agentSign: '',
          agentDate: ''
        });
      } else {
        setToast({
          show: true,
          message: data.message || 'Failed to submit field analysis. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting field analysis:', error);
      setToast({
        show: true,
        message: 'Error: Failed to connect to server. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-2 sm:mt-5 h-full flex justify-center px-2 sm:px-4 lg:px-6">
      {/* Toast Notification */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}

      <Card extra="w-full max-w-5xl p-4 sm:p-6 lg:p-8">
        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-navy-700 dark:text-white mb-1">
                  Raw Material Analysis for Field
                </h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{form.analystName || '-'}</p>
              </div>
              {(() => {
                const qualityData = calculateQualityRating(form);
                return qualityData.qualityRating ? (
                  <div className="flex sm:justify-end">
                    <QualityRatingBadge 
                      defectiveRate={qualityData.defectiveRate}
                      qualityRating={qualityData.qualityRating}
                      ratingColor={qualityData.ratingColor}
                    />
                  </div>
                ) : null;
              })()}
            </div>
          </div>

          {/* General Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <MdDateRange className="text-xl" />
              General Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date <span className="text-red-500">*</span></label>
                <input 
                  name="date" 
                  value={form.date} 
                  onChange={handleInput} 
                  type="date" 
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Truck Order No.</label>
                <input 
                  name="orderNo" 
                  value={form.orderNo} 
                  onChange={handleInput} 
                  type="text"
                  placeholder="Enter order number"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">RCN Type <span className="text-red-500">*</span></label>
                <select 
                  name="region" 
                  value={form.region} 
                  onChange={handleInput} 
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  required
                >
                  <option value="">Select RCN type</option>
                  <option value="Conventional">Conventional</option>
                  <option value="Organic">Organic</option>
                  <option value="Fair Trade RCN">Fair Trade RCN</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Province/City <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="provinceCity"
                  value={form.provinceCity}
                  onChange={handleInput}
                  list="provinceCityOptions"
                  placeholder="Select or type province/city"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  required
                />
                <datalist id="provinceCityOptions">
                  {provinceCities.map((city) => (
                    <option key={city} value={city} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Analyst Name <span className="text-red-500">*</span></label>
                <input 
                  name="analystName" 
                  value={form.analystName} 
                  readOnly
                  type="text"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 dark:bg-navy-700 dark:text-white font-semibold" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Name/Association Name <span className="text-red-500">*</span></label>
                <input 
                  name="nameAgent" 
                  value={form.nameAgent} 
                  onChange={handleInput} 
                  type="text"
                  placeholder="Enter name/agent"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Truck No.</label>
                <input 
                  name="truckNo" 
                  value={form.truckNo} 
                  onChange={handleInput} 
                  type="text"
                  placeholder="e.g., GE 972941"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Bags (No)</label>
                <input 
                  name="bagsNo" 
                  value={form.bagsNo} 
                  onChange={handleInput} 
                  type="number"
                  min="0"
                  placeholder="Enter number of bags"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Driver's Name</label>
                <input 
                  name="driverName" 
                  value={form.driverName} 
                  onChange={handleInput} 
                  type="text"
                  placeholder="Enter driver's name"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Driver's Contact</label>
                <input 
                  name="driverContact" 
                  value={form.driverContact} 
                  onChange={handleInput} 
                  type="text"
                  placeholder="Enter driver's contact"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Outturn (LBS) <span className="text-gray-500 text-xs">(Auto-calculated)</span></label>
                <input 
                  name="outturnLbs" 
                  value={form.outturnLbs || '0.00'} 
                  readOnly
                  type="text"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 dark:bg-navy-700 dark:text-white font-semibold" 
                  placeholder="Auto-calculated"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Formula: (Good + (Spotted + Immature) / 2) × 0.176
                </p>
              </div>
            </div>
          </div>

          {/* Material Analysis Data Section */}
          <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <MdAssignment className="text-xl" />
              Material Analysis Data
            </h2>
            
            {/* Departure Checkbox */}
            {/* <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="departure"
                  checked={form.departure}
                  onChange={handleInput}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-navy-700 dark:text-white">Departure</span>
              </label>
            </div> */}

            {/* Analysis Table */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-700 mb-4">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-navy-700">
                <thead className="bg-gray-50 dark:bg-navy-800">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Parameter</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Value (g)</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Percentage (%)</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-navy-900 divide-y divide-gray-100 dark:divide-navy-800">
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Nut Count</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        name="nutCount"
                        value={form.nutCount}
                        onChange={handleInput}
                        placeholder="Enter nut count"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">-</td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Good (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="goodG"
                        value={form.goodG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.goodG ? ((parseFloat(form.goodG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Spotted (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="spottedG"
                        value={form.spottedG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.spottedG ? ((parseFloat(form.spottedG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Immature (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="immatureG"
                        value={form.immatureG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.immatureG ? ((parseFloat(form.immatureG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Impurities (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number" 
                        min="0"
                        step="0.01"
                        name="impuritiesG"
                        value={form.impuritiesG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.impuritiesG ? ((parseFloat(form.impuritiesG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Bored (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="boredG"
                        value={form.boredG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.boredG ? ((parseFloat(form.boredG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Rotten (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="rottenG"
                        value={form.rottenG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.rottenG ? ((parseFloat(form.rottenG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Void (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="voidG"
                        value={form.voidG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.voidG ? ((parseFloat(form.voidG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Oil (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="oilG"
                        value={form.oilG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.oilG ? ((parseFloat(form.oilG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Shell (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="shellG"
                        value={form.shellG}
                        onChange={handleInput}
                        placeholder="Enter weight in g"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                      {form.shellG ? ((parseFloat(form.shellG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-navy-800">
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-bold text-navy-700 dark:text-white">Total (g)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="text"
                        name="totalG"
                        value={form.totalG}
                        readOnly
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm bg-gray-100 dark:bg-navy-700 dark:text-white font-semibold"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-bold text-navy-700 dark:text-white">
                      {form.totalG ? ((parseFloat(form.totalG) / 1000) * 100).toFixed(2) : '0.00'}%
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">TDR (%)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="text"
                        name="tdrPercent"
                        value={form.tdrPercent}
                        readOnly
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm bg-gray-100 dark:bg-navy-700 dark:text-white font-semibold"
                        placeholder="Auto-calculated"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">-</td>
                  </tr>
                  <tr>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">Moisture (%)</td>
                    <td className="px-2 sm:px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        name="moisturePercent"
                        value={form.moisturePercent}
                        onChange={handleInput}
                        placeholder="Enter moisture %"
                        className="w-full sm:w-32 rounded border border-gray-200 px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                      />
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional Comments */}
          <div>
            <label className="block text-sm font-medium mb-1">Additional Comments</label>
            <textarea
              name="additionalComments"
              value={form.additionalComments}
              onChange={handleInput}
              rows={4}
              placeholder="Enter any additional comments or observations..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Quality Rating Section */}
          {(() => {
            const qualityData = calculateQualityRating(form);
            return (
              <div>
                <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <MdCheckCircle className="text-xl" />
                  Quality Rating
                </h2>
                <div className="bg-gray-50 dark:bg-navy-800 rounded-lg p-4 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <span className="font-medium text-navy-700 dark:text-white">Total Defective Rate:</span> {qualityData.defectiveRate}%
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Formula: [ (1/2 × (Spotted + Immature)) + Rotten + Void + Impurities + Bored + Oily ] / 1000g × 100%
                      </p>
                    </div>
                    {qualityData.defectiveRate && (
                      <QualityRatingBadge 
                        defectiveRate={qualityData.defectiveRate}
                        qualityRating={qualityData.qualityRating || 'No rating'}
                        ratingColor={qualityData.ratingColor || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'}
                      />
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Rating Scale:</span> 0-6.9% (Very good) | 7-10.9% (Good) | 11-15.9% (Regular) | 16-20.9% (Bad) | 21%+ (Very bad)
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Signatures Section */}
          {/* <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <MdSupervisorAccount className="text-xl" />
              Signatures
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Agent Name</label>
                <input 
                  name="agentSign" 
                  value={form.agentSign} 
                  onChange={handleInput} 
                  type="text"
                  placeholder="Agent Name"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Agent's Date</label>
                <input 
                  name="agentDate" 
                  value={form.agentDate} 
                  onChange={handleInput} 
                  type="date"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>
            </div>
          </div> */}

          {/* Submit Button */}
          <div className="flex justify-center sm:justify-end pt-4">
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={`w-full sm:w-auto rounded-lg px-6 py-3 font-semibold transition-colors text-base sm:text-sm ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Field Analysis'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default FieldAnalysis;
